// /clubs/:clubId: mirrors screens/ClubDetailsScreen.js (tabs About / Events / Gallery / Members).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Page } from "../components/Band";
import Icon from "../components/Icon";
import Modal, { ModalBody, ModalFooter } from "../components/Modal";
import { Button, Chip, ClubLogo, Content, Empty, PageSpinner, Pill } from "../components/primitives";
import { ClubService } from "../../../services/clubService";
import { clubShareLink } from "../../../utils/deepLinks";
import { trackClubShareOpened, trackClubViewed } from "../../../services/clubAnalyticsService";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { useToast } from "../components/Toast";
import { useCopyText } from "../lib/clipboard";
import useClubApply from "../hooks/useClubApply";
import useClubDetail from "../components/clubs/useClubDetail";
import ClubAbout from "../components/clubs/ClubAbout";
import ClubEvents from "../components/clubs/ClubEvents";
import ClubGallery from "../components/clubs/ClubGallery";
import ClubMembers from "../components/clubs/ClubMembers";
import JoinButton from "../components/clubs/JoinButton";
import ConfirmModal from "../components/manage/ConfirmModal";
import { categoryEmoji } from "../../../utils/categoryEmoji";
import "../styles/clubs-web.css";

const TABS = ["about", "events", "gallery", "members"];

export default function ClubPage() {
  const { clubId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const copy = useCopyText();
  const { user } = useAuth();
  const { loadUserClubs } = useData();
  const seed = location.state?.club;
  const d = useClubDetail(clubId, user, seed);
  const { club, mem } = d;

  const [tab, setTab] = useState("about");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const leaveTimer = useRef(null);

  // Per-club resets + view analytics (ClubDetailsScreen.js:183-186).
  useEffect(() => {
    setTab("about");
    setWithdrawOpen(false);
    setMembersLoaded(false);
    trackClubViewed(clubId, location.state?.source || "unknown");
    return () => clearTimeout(leaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const refresh = useCallback(() => { loadUserClubs(); d.refreshUser(); }, [loadUserClubs, d]);
  const { applyingClubId, onJoinPress } = useClubApply({ user, refresh });

  // Members tab loads lazily (loadMembersData :524).
  useEffect(() => {
    if (tab !== "members" || membersLoaded || membersLoading || !club) return undefined;
    let alive = true;
    setMembersLoading(true);
    ClubService.getClubMembers(clubId).then(({ data, error }) => {
      if (!alive) return;
      if (!error && data) { d.setMembers(data); d.setMemberCount(data.length); }
      setMembersLoaded(true);
      setMembersLoading(false);
    });
    return () => { alive = false; setMembersLoading(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, membersLoaded, clubId, !!club]);

  const back = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate("/clubs", { replace: true });
  };

  const share = () => {
    trackClubShareOpened(clubId);
    copy(clubShareLink(clubId), "Link copied");
  };

  // handleLeaveClub (:544-603): optimistic flip, restore on failure, reload after 500 ms.
  const clubIdRef = useRef(clubId);
  clubIdRef.current = clubId;
  const leave = async () => {
    if (!user) return;
    const startedFor = clubId;
    const prev = { mem, members: d.members, count: d.memberCount };
    setLeaving(true);
    d.setMem((m) => ({ ...m, isMember: false, isAdmin: false, isStrict: false }));
    d.setMembers((ms) => ms.filter((m) => m.user_id !== user.uid));
    d.setMemberCount((n) => Math.max(0, (n ?? 0) - 1));
    setLeaveOpen(false);
    const { error } = await ClubService.leaveClub(clubId, user.uid);
    setLeaving(false);
    if (clubIdRef.current !== startedFor) { if (!error) loadUserClubs(); return; } // route moved to another club mid-flight
    if (error) {
      d.setMem(prev.mem);
      d.setMembers(prev.members);
      d.setMemberCount(prev.count);
      toast("Failed to leave club. Please try again.", "alert");
      return;
    }
    toast("You have left the club");
    loadUserClubs();
    leaveTimer.current = setTimeout(() => { d.reload(); setMembersLoaded(false); }, 500);
  };

  if (d.loading && !club) return <Page><PageSpinner /></Page>;
  if (d.error && !club) {
    return (
      <Page>
        <Content flat>
          <Empty icon="alert" title="Couldn't load this club">
            <p className="meta" style={{ marginBottom: 12 }}>{d.error}</p>
            <div className="row" style={{ justifyContent: "center" }}>
              <Button variant="ghost" onClick={back} data-testid="clubDetails.errorBack">Back</Button>
              <Button onClick={d.reload} data-testid="clubDetails.retry">Retry</Button>
            </div>
          </Empty>
        </Content>
      </Page>
    );
  }
  if (!club) return null;

  const joinState = mem.isMember ? "joined" : !club.is_verified ? "inactive" : mem.hasApplied ? "applied" : "apply";
  const busy = applyingClubId === club.id;
  const count = d.memberCount ?? club.member_count ?? 0;
  const memberLine = `${count} member${count === 1 ? "" : "s"}`;

  return (
    <Page>
      <div className="hero">
        <div className="rings" />
        <div className="herobar">
          <Chip iconOnly glass aria-label="Back" data-testid="clubDetails.back" onClick={back}><Icon name="left" /></Chip>
          <span className="grow" />
          {mem.isAdmin && <Chip icon="shield" data-testid="clubDetails.manage" onClick={() => navigate(`/manage/${club.id}`)}>Manage Club</Chip>}
          <Chip glass icon="share" data-testid="clubDetails.share" onClick={share}>Share</Chip>
        </div>
      </div>
      {d.error && (
        <div className="row" role="alert" style={{ gap: 10, padding: "10px 20px", justifyContent: "center", background: "var(--wash)" }}>
          <span className="meta">Couldn&apos;t refresh this club: {d.error}</span>
          <Button size="sm" variant="sec" onClick={d.reload} data-testid="clubDetails.refreshRetry">Retry</Button>
        </div>
      )}
      <div className="idrow">
        <ClubLogo club={club} size={112} />
        <div className="grow" style={{ paddingBottom: 6, minWidth: 0 }}>
          <h1>{club.name}</h1>
          <div className="meta">
            {[club.category && `${categoryEmoji(club.category)} ${club.category}`.trim(), memberLine].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="row" style={{ paddingBottom: 6 }}>
          {joinState === "joined" && !mem.isStrict && (
            <Button variant="ghost" disabled={leaving} onClick={() => setLeaveOpen(true)} data-testid="clubDetails.leave">Leave</Button>
          )}
          {joinState === "joined" && mem.isStrict && <Pill tone="green" icon="check">Joined</Pill>}
          {joinState === "applied" && (
            <JoinButton state="applied" size="" busy={busy} onPress={() => setWithdrawOpen(true)} testId="clubDetails.withdraw" />
          )}
          {joinState === "apply" && (
            <Button style={{ minWidth: 140 }} disabled={busy} onClick={() => onJoinPress(club, "apply")} data-testid="clubDetails.apply">{busy ? "Applying…" : "Apply"}</Button>
          )}
          {joinState === "inactive" && <Pill tone="gray">Not active</Pill>}
        </div>
      </div>

      <Content flat>
        <div className="tabs" role="tablist" data-testid="clubDetails.tab">
          {TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} data-testid={`clubDetails.tab.${t}`} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ paddingTop: 18 }}>
          {tab === "about" && <ClubAbout club={club} isMember={mem.isMember} isAdmin={mem.isAdmin} />}
          {tab === "events" && (
            <ClubEvents club={club} user={user} isMember={mem.isMember} resolved={mem.resolved} payloadEvents={d.payloadEvents} />
          )}
          {tab === "gallery" && <ClubGallery gallery={club.gallery} />}
          {tab === "members" && (
            <ClubMembers
              clubId={club.id}
              members={d.members}
              loading={membersLoading && !membersLoaded}
              uid={user?.uid}
              isAdmin={mem.isAdmin}
              isStrict={mem.isStrict}
              onRoleChanged={() => { d.reload(); setMembersLoaded(false); }}
            />
          )}
        </div>
      </Content>

      {withdrawOpen && (
        <ConfirmModal
          title="Withdraw application?"
          confirmLabel="Withdraw"
          danger
          testId="clubDetails.withdrawConfirm"
          onClose={() => setWithdrawOpen(false)}
          onConfirm={() => { setWithdrawOpen(false); onJoinPress(club, "applied"); }}
        >
          Your application to {club.name} will be removed. You can apply again later.
        </ConfirmModal>
      )}

      {leaveOpen && (
        <Modal title={`Leave ${club.name}?`} size="narrow" onClose={() => setLeaveOpen(false)} testId="clubDetails.leaveConfirm">
          <ModalBody><p className="muted">Are you sure you want to leave this club?</p></ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setLeaveOpen(false)} data-testid="clubDetails.leaveConfirm.cancel">Cancel</Button>
            <Button variant="dangerSolid" onClick={leave} data-testid="clubDetails.leaveConfirm.confirm">Leave club</Button>
          </ModalFooter>
        </Modal>
      )}
    </Page>
  );
}
